import {
  Button,
  Card,
  PageBody,
  Spacer,
  Stack,
  TextBody,
  TextCaption,
  TextTitle,
} from '@pypestream/design-system';
import { useTranslation } from '@pypestream/translations';
import { FC, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { Loader } from '../../../../components';
import { OrganizationRoleNames, useAuthRole } from '../../../../hooks';
import { useManagerCtxSelector } from '../../../../xstate/app.xstate';
import { IPage } from '../../../types';
import {
  fetchOrgSecretScopes,
  fetchOrgSecrets,
  type MaestroError,
  type OrgSecret,
  type OrgSecrets,
  reasonFor,
  type ScopeProject,
} from './maestro-org-api';
import { OrganizationTabs } from './organization-tabs';
import { OrgSecretDeleteDialog } from './org-secret-delete-dialog';
import { OrgSecretModal, type OrgSecretModalMode } from './org-secret-modal';
import {
  countKinds,
  filterSecretRows,
  isFiltered,
  NO_FILTERS,
  orgSecretRows,
  type SecretFilters,
} from './org-secret-rows';
import { scopeOptions } from './org-secret-scope';
import { useSecretsText } from './org-secrets-text';
import { OrgSecretsTable } from './org-secrets-table';
import { OrgSecretsToolbar } from './org-secrets-toolbar';
import { NoAccessCard } from './org-tab-parts';
import { useIsRootOrg } from './use-is-root-org';
import { useMaestroOrgPermissions } from './use-maestro-org-permissions';
import './maestro-org.scss';

const NONE: OrgSecrets = { own: [], scoped: [], inherited: [] };

export const MaestroOrgSecretsPage: FC<IPage> = () => {
  const { t } = useTranslation();
  const text = useSecretsText();
  const { org_id: orgId = '' } = useParams();
  const orgName =
    useManagerCtxSelector(
      (ctx) => ctx.orgDetails?.name ?? ctx.childOrgs?.currentOrg?.name
    ) ?? '';
  const isRootOrg = useIsRootOrg(orgId);
  const permissions = useMaestroOrgPermissions(orgId);
  const canManage = Boolean(permissions?.canManageSecrets);
  const isSuperAdmin = useAuthRole([OrganizationRoleNames.SUPER_ADMIN]);
  const canManageKeys = isSuperAdmin && isRootOrg;

  const [secrets, setSecrets] = useState<OrgSecrets | null>(null);
  const [projects, setProjects] = useState<ScopeProject[]>([]);
  const [error, setError] = useState<MaestroError | null>(null);
  const [filters, setFilters] = useState<SecretFilters>(NO_FILTERS);
  const [modal, setModal] = useState<OrgSecretModalMode | null>(null);
  const [deleting, setDeleting] = useState<OrgSecret | null>(null);

  const load = useCallback(async () => {
    try {
      setSecrets(await fetchOrgSecrets(orgId));
    } catch (e) {
      setError(reasonFor(e).reason);
      setSecrets(NONE);
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Without the picker the list still works: only Organization scope is offered.
  useEffect(() => {
    let current = true;
    fetchOrgSecretScopes(orgId).then(
      (loaded) => current && setProjects(loaded),
      () => current && setProjects([])
    );
    return () => {
      current = false;
    };
  }, [orgId]);

  const forbidden =
    error === 'forbidden' ||
    (permissions !== null && !permissions.canViewSecrets);

  const rows = secrets
    ? orgSecretRows(
        secrets.own,
        secrets.scoped,
        isRootOrg ? [] : secrets.inherited
      )
    : [];
  const shown = filterSecretRows(rows, filters);
  const filtered = isFiltered(filters);
  const scopes = scopeOptions(projects);

  return (
    <>
      <PageBody background="none">
        <TextTitle test-id="org-details-title">{orgName}</TextTitle>
        <OrganizationTabs active="secrets" permissions={permissions} />
        <Spacer size="large" />
        {forbidden ? (
          <NoAccessCard />
        ) : secrets === null || permissions === null ? (
          <Loader relative />
        ) : (
          <Card
            size="large"
            shadow="none"
            border="default"
            test-id="org-secrets"
          >
            <div className="c-org-head">
              <div>
                <h2 className="c-org-card-title">{text('title')}</h2>
                <TextCaption variant="secondary">
                  {isRootOrg
                    ? text('lede.root')
                    : text('lede.child', { org: orgName })}
                </TextCaption>
              </div>
              <Stack alignItems="center" gutter="small" nowrap>
                {canManage && (
                  <Button
                    test-id="org-secrets-add"
                    onClick={() => setModal({ type: 'add' })}
                  >
                    {text('add')}
                  </Button>
                )}
              </Stack>
            </div>
            <Spacer size="medium" />
            {error && (
              <>
                <TextCaption variant="danger" role="alert">
                  {t(`manager/organizations:orgTabs.errors.${error}`)}
                </TextCaption>
                <Spacer size="small" />
              </>
            )}
            <OrgSecretsToolbar
              filters={filters}
              onChange={setFilters}
              scopes={scopes}
              orgName={orgName}
              hasParentRows={rows.some((row) => row.source === 'parent')}
              counts={countKinds(shown)}
            />
            <Spacer size="medium" />
            {shown.length === 0 ? (
              <Stack alignItems="center" gutter="small">
                <TextBody variant="secondary" test-id="org-secrets-empty">
                  {text(filtered ? 'noMatch' : 'empty')}
                </TextBody>
                {filtered && (
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => setFilters(NO_FILTERS)}
                  >
                    {text('filters.clear')}
                  </Button>
                )}
              </Stack>
            ) : (
              <OrgSecretsTable
                rows={shown}
                orgName={orgName}
                canManage={canManage}
                canManageKeys={canManageKeys}
                onValue={(secret) => setModal({ type: 'value', secret })}
                onEdit={(secret) => setModal({ type: 'edit', secret })}
                onDelete={setDeleting}
              />
            )}
          </Card>
        )}
      </PageBody>
      <OrgSecretModal
        orgId={orgId}
        mode={modal}
        isRootOrg={isRootOrg}
        canMarkPypestreamKey={canManageKeys}
        scopes={scopes}
        projects={projects}
        defaultScope={filters.scope === 'all' ? 'org' : filters.scope}
        onClose={() => setModal(null)}
        onSaved={() => {
          setModal(null);
          void load();
        }}
      />
      <OrgSecretDeleteDialog
        orgId={orgId}
        secret={deleting}
        onCancel={() => setDeleting(null)}
        onDeleted={() => {
          setDeleting(null);
          void load();
        }}
      />
    </>
  );
};
